import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import TopBar from '../../../components/TopBar';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, map } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextInput from '../../../components/CustomTextInput';
import CountrySelect from '../../../components/form/CountrySelect';
import UAESubdivisionSelect from '../../../components/form/UAESubdivisionSelect';
import RegistrationTypeSelect from '../../../components/form/RegistrationTypeSelect';
import PartyIdentificationSchemeSelect from '../../../components/form/PartyIdentificationSchemeSelect';
import { fetchParticipantRequest } from '../../../api/participants/participantsSlice';
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getSubdivisionOptionByCode,
  getIso6523IcdOptionByCode,
} from '../../../api/codelist/codeListsSlice';
import TaxSchemeSelect from '../../../components/form/TaxSchemeSelect';
import { SupplierValidation } from '../../../validations/SupplierValidation';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import LogoUpload from '../../../components/LogoUpload';
import { uploadLogoToCloud } from '../../../helper/UploadLogoToCloud';
import { showSnackbar } from '../../../api/snackbarSlice';
import { UAE_COUNTRY_CODE } from '../../../constants/defaults';
import { normalizePartyTaxSchemeForSubmit } from '../../../utils/partyTaxSchemeSubmit';
import { mapPartyIdentificationForSubmit } from '../../../utils/partyIdentificationSubmit';
import TrnVatField from '../../common/components/TrnVatField';

export default function SupplierForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = false,
  isLoading = false,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const countryOption = useSelector(getCountryOptionByCode(initialData.postal_address?.country_code));
  const subdivisionOption = useSelector(getSubdivisionOptionByCode(initialData.postal_address?.country_subentity));
  const registrationTypeOption = useSelector(getRegistrationTypeOptionByCode(initialData.registration_type));
  const passportIssuingCountryOption = useSelector(getCountryOptionByCode(initialData.passport_issuing_country_code));
  const subscription = useSelector((state) => state.subscription.subscription);
  const schemeAgencyIdOption = useSelector(getRegistrationTypeOptionByCode(initialData.scheme_agency_id));
  const partyIdentificationSchemeOption = useSelector(
    getIso6523IcdOptionByCode(initialData.party_identification_scheme_id),
  );

  const participant = useSelector((state) => state.participants.participant);

  const [participantId, setParticipantId] = useState(initialData.participant_id || '');
  const [peppolParticipant, setPeppolParticipant] = useState(initialData.peppol_participant || {});
  const [loading, setLoading] = useState(false);
  const [loadingParticipant, setLoadingParticipant] = useState(false);
  const [file, setFile] = useState(null);
  const isTrnVerificationEnabled = subscription?.features?.enable_trn_verification;

  const { suppliers: s, validations, language } = useTranslatedMessages();

  const {
    register,
    control,
    getValues,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(SupplierValidation(validations?.supplier)),
    defaultValues: {
      ...initialData,
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name: initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: countryOption,
        country_subentity:
          initialData.postal_address?.country_code == UAE_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      party_identification_id: initialData.party_identification_id ?? '',
      party_identification_scheme_id: partyIdentificationSchemeOption ?? null,
      registration_type: registrationTypeOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      peppol_participant: initialData.peppol_participant ? initialData.peppol_participant : {},
      participant_id:
        initialData?.endpoint_id && initialData?.endpoint_scheme_id
          ? `${initialData?.endpoint_scheme_id}:${initialData?.endpoint_id}`
          : '',
      party_tax_scheme: {
        tax_scheme: initialData.party_tax_scheme?.tax_scheme ?? '',
        company_id: initialData.party_tax_scheme?.company_id ?? '',
      },
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  useEffect(() => {
    const watchParticipantId = watch((value, { name }) => {
      if (name === 'participant_id') {
        setParticipantId(value.participant_id);
        setLoadingParticipant(true);
      }
    });
    return () => watchParticipantId.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setParticipantId(getValues('participant_id'));
    if (participantId) {
      handleFetchParticipants();
    }
  }, [participantId]);

  const handleFetchParticipants = async () => {
    if (!participantId) return;
    if (!subscription?.features?.enable_peppol) {
      setLoading(false);
      setLoadingParticipant(false);
      return;
    }
    setLoading(true);
    setLoadingParticipant(true);

    const [identifier_code, rest] = participantId.split(':');
    const match = rest ? rest.match(/(\d{2})(.*)/) : [];
    const [special_identifier, identifier] = match ? match.slice(1) : [];

    try {
      if (subscription?.features?.enable_peppol)
        await dispatch(
          fetchParticipantRequest({
            identifier_code,
            identifier,
            special_identifier,
          }),
        );
    } catch (error) {
      console.error('Error fetching participant:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPeppolEmpty = !peppolParticipant?.endpoint_id;

  useEffect(() => {
    setLoadingParticipant(false);
    if (!isEmpty(participant)) {
      const match = participant.idValue?.match(/(\d{2})(.*)/);
      const [special_identifier, identifier] = match ? match.slice(1) : [];

      setPeppolParticipant({
        scheme: participant?.idScheme,
        identifier: identifier,
        identifier_code: participant?.idType,
        special_identifier: special_identifier,
        endpoint_id: participant?.endpoint[0]?.url,
      });
    }
    if (participantId === '') {
      setPeppolParticipant({});
    }
  }, [participant]);

  const registrationType = useWatch({ control, name: 'scheme_agency_id' });
  const registrationNumberLabel = registrationType
    ? `${registrationType.label} (${registrationType?.code})`
    : s?.form?.registration_number || 'Registration Number';

  const handleCountryChange = (value) => {
    setValue('postal_address.country_subentity', value?.code === UAE_COUNTRY_CODE ? null : '');
    setValue('scheme_agency_id', null);
    setValue('company_id', '');
    setValue('authority_name', null);
    setValue('passport_issuing_country_code', null);
  };

  const handleRegisterType = () => {
    setValue('company_id', '');
    setValue('authority_name', null);
    setValue('passport_issuing_country_code', null);
  };

  const handleTaxSchemeChange = () => {
    trigger(['party_tax_scheme.company_id', 'party_tax_scheme.tax_scheme']);
  };

  const handleFormSubmit = async (data) => {
    setLoading(true);
    let logoUrl = initialData?.logo_url ?? '';

    if (file) {
      logoUrl = await uploadLogoToCloud(file);
      if (!logoUrl) {
        dispatch(showSnackbar({ message: 'Failed to upload logo', severity: 'error' }));
        setLoading(false);
        return;
      }
    }
    setLoading(false);

    const submitData = {
      ...data,
      endpoint_scheme_id: data.participant_id?.split(':')[0],
      endpoint_id: data.participant_id?.split(':')[1],
      scheme_agency_id: data.scheme_agency_id?.code,
      logo_url: logoUrl,
      tin: data.tin,
      postal_address: {
        ...data.postal_address,
        country_code: data.postal_address?.country_code?.code,
        country: data.postal_address?.country_code?.label,
        country_subentity:
          data.postal_address?.country_code?.code === UAE_COUNTRY_CODE
            ? data.postal_address?.country_subentity?.code
            : data.postal_address?.country_subentity,
      },
      passport_issuing_country_code: data.passport_issuing_country_code?.code,
      ...mapPartyIdentificationForSubmit(data),
    };

    const isUAE = data.postal_address?.country_code?.code === UAE_COUNTRY_CODE;
    if (!isUAE) {
      delete submitData.party_tax_scheme;
      delete submitData.tin;
    } else {
      const normalized = normalizePartyTaxSchemeForSubmit(submitData.party_tax_scheme);
      if (normalized) {
        submitData.party_tax_scheme = normalized;
      } else {
        delete submitData.party_tax_scheme;
      }
    }

    onSubmit(submitData);
  };

  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      {!readOnly && (
        <Box sx={{ pt: 2, pb: 4 }}>
          <Grid
            container
            sx={{
              display: 'block',
            }}
          >
            <Grid xs={12}>
              <TopBar title={s?.form?.title || 'Supplier Form'} identifier={initialData?.profile_id}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
                  <Button
                    data-testid="supplier-profile-cancel-button"
                    key={'cancel'}
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isLoading}
                  >
                    {s?.form?.cancel || 'Cancel'}
                  </Button>
                  <Button
                    data-testid="supplier-profile-save-button"
                    key={'save'}
                    variant="contained"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {s?.form?.save || 'Save'}
                  </Button>
                </Box>
              </TopBar>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        <Grid container spacing={2}>
          <Grid container spacing={2} size={{ xs: 4, sm: 8 }}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="name-input"
                name="name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.name || 'Name'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="business_name-input"
                name="party_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.business_name || 'Business Name'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="business_name_in_local_language-input"
                name="party_name_in_local_language"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.business_name_local || 'Business Name in Local Language'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <CustomTextInput
                data-testid="email-input"
                name="email"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.email || 'Email'}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 4, sm: 4 }}>
            <LogoUpload initialLogoUrl={initialData?.logo_url} onFileChange={setFile} readOnly={readOnly} />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {s?.form?.address_section || 'Address'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="street_name-input"
              name="postal_address.street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.street_name || 'Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="additional_street_name-input"
              name="postal_address.additional_street_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.additional_street_name || 'Additional Street Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="city_name-input"
              name="postal_address.city_name"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.city_name || 'City Name'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="postal_address.address_line"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.address_line || 'Address Line'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountrySelect
              data-testid="postal_address.country_code-select"
              name="postal_address.country_code"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.country || 'Country'}
              onChange={handleCountryChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {useWatch({ control, name: 'postal_address.country_code' })?.code === UAE_COUNTRY_CODE ? (
              <UAESubdivisionSelect
                errors={errors}
                control={control}
                data-testid="uae-subentity-select"
                name="postal_address.country_subentity"
                readOnly={readOnly}
                label={s?.form?.uae_subentity || 'UAE Subentity'}
              />
            ) : (
              <CustomTextInput
                data-testid="country-subentity-input"
                name="postal_address.country_subentity"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.country_subentity || 'Country Subentity'}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="postal_zone-input"
              name="postal_address.postal_zone"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.postal_zone || 'Postal Zone'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-tetsid="telephone-input"
              name="telephone"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.telephone || 'Telephone'}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
          {s?.form?.business_identifiers_section || 'Business Identifiers'}
          <Divider></Divider>
        </Typography>

        <Grid container spacing={2}>
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TaxSchemeSelect
                data-testid="tax_scheme-select"
                name="party_tax_scheme.tax_scheme"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.tax_scheme || 'Tax Scheme'}
                onTaxSchemeChange={handleTaxSchemeChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {useWatch({ control, name: 'postal_address.country_code' })?.code === UAE_COUNTRY_CODE &&
              isTrnVerificationEnabled ? (
                <TrnVatField
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  watch={watch}
                  readOnly={readOnly}
                  label={s?.form?.vat || 'VAT'}
                  labels={s?.form?.trn_verify ?? {}}
                  validationScope="supplier"
                />
              ) : (
                <CustomTextInput
                  data-testid="tax-identifier-input"
                  name="party_tax_scheme.company_id"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={s?.form?.vat || 'VAT'}
                  inputProps={{ maxLength: 15 }}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="tin-input"
                name="tin"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.tin || 'TIN'}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
          </>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RegistrationTypeSelect
              data-testid="registration-type-select"
              errors={errors}
              readOnly={readOnly}
              control={control}
              name="scheme_agency_id"
              onChange={handleRegisterType}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="registration-number-input"
              name="company_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={registrationNumberLabel}
            />
          </Grid>
          {registrationType?.code === 'TL' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="registration_authority-input"
                name="authority_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.registration_authority || 'Registration Authority'}
              />
            </Grid>
          )}
          {registrationType?.code === 'PAS' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect
                data-testid="passport_issuing_country_code-select"
                name="passport_issuing_country_code"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={s?.form?.passport_issuing_country || 'Passport Issuing Country'}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <PartyIdentificationSchemeSelect
              data-testid="party_identification_scheme_id-select"
              name="party_identification_scheme_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.party_identification_scheme_id ?? 'Party Identification Scheme'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="party_identification_id-input"
              name="party_identification_id"
              control={control}
              errors={errors}
              readOnly={readOnly}
              label={s?.form?.party_identification_id ?? 'Party Identification'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              name="participant_id"
              control={control}
              errors={errors}
              label={s?.form?.participant_id ?? 'Participant ID'}
              placeholder="0235:234234234"
              readOnly={readOnly}
            />
          </Grid>
        </Grid>

        {/*{(edit || readOnly) && (*/}
        {/*  <PeppolDetails*/}
        {/*    control={control}*/}
        {/*    errors={errors}*/}
        {/*    getValues={getValues}*/}
        {/*    setParticipantId={setParticipantId}*/}
        {/*    handleFetchParticipants={handleFetchParticipants}*/}
        {/*    loading={loading}*/}
        {/*    readOnly={readOnly}*/}
        {/*    loadingParticipant={loadingParticipant}*/}
        {/*    isPeppolEmpty={isPeppolEmpty}*/}
        {/*    peppolParticipant={peppolParticipant}*/}
        {/*    peppolLabels={s?.form}*/}
        {/*    peppolParticipantDescription={*/}
        {/*      s?.form?.peppol_description ||*/}
        {/*      'Fetch Peppol participant ID to send E-Documents directly over Peppol network.'*/}
        {/*    }*/}
        {/*  />*/}
        {/*)}*/}
      </Box>
    </Paper>
  );
}
